import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { setToast } from '@/entities/toast'
import { logoutUser } from '@/entities/user'
import { postApiAuthResendVerificationEmail } from '@/shared/apis/fastmile/generated/auth/auth'
import { Button } from '@/shared/components'
import { Icon } from '@/shared/components/Icon/Icon'
import { getJwtTokenData } from '@/shared/lib/JWTTokenHelpers'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { Box, styled, Typography } from '@mui/material'

import { StyledAlert } from '../Registration/StyledRegistration'

export const VerifyEmailPage: React.FC = () => {
  const [countdown, setCountdown] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const session = useAppSelector(state => state.session)

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000)

      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleResendVerification = async () => {
    if (countdown > 0 || isResending || !session.accessToken) return

    try {
      setIsResending(true)

      const userId = getJwtTokenData(session.accessToken).UserId

      await postApiAuthResendVerificationEmail({
        userId: Number(userId),
      })

      setCountdown(30)
    } catch (error) {
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Failed to Resend',
        content: 'There was an error sending the verification email. Please try again.',
      }))
    } finally {
      setIsResending(false)
    }
  }

  const handleGoToLogin = () => {
    dispatch(logoutUser()).then(() => {
      navigate('/', { replace: true })
    })
  }

  useEffect(() => {
    if (session.isEmailVerified) {
      navigate('/onboarding', { replace: true })
    }
  }, [session, navigate])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <StyledBox>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Icon
            icon={faEnvelope}
            sx={{
              fontSize: '2rem',
              color: 'primary.main',
            }}
          />
        </Box>

        <Typography variant="body1" align="center" sx={{ fontSize: pxToRem(24), mb: 2 }}>
          Please verify your email address <strong>{session.email}</strong> to continue using Fastmile.
        </Typography>

        <StyledAlert severity="info" sx={{ mb: 3 }}>
          If you don't see the verification email in your inbox, please check your spam or junk folder.
        </StyledAlert>

        <Button
          fullWidth
          primary
          size="medium"
          onClick={handleResendVerification}
          disabled={countdown > 0 || isResending}
          sx={{ mb: 2 }}
        >
          {isResending
            ? 'Sending...'
            : countdown > 0
              ? `Resend Email (${countdown}s)`
              : 'Resend Verification Email'}
        </Button>

        <Button
          fullWidth
          text
          size="medium"
          onClick={handleGoToLogin}
        >
          Back to Login
        </Button>

        <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 3 }}>
          Need help? Contact our support team for assistance.
        </Typography>
      </StyledBox>
    </Box>
  )
}

export const StyledBox = styled(Box)`
  width: 100%;
  max-width: ${props => props.theme.typography.pxToRem(480)};
  padding: ${props => props.theme.typography.pxToRem(32)};
  border-radius: ${props => props.theme.typography.pxToRem(16)};
  background-color: ${props => props.theme.palette.background.paper};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  ${props => props.theme.breakpoints.up('lg')} {
    width: 50%;
  }
`